import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Zap, Bell, LogOut, Trash2, X, Sun, Moon, Monitor, Flame, Pencil, Check } from "lucide-react";
import { Tooltip } from "./Tooltip.tsx";
import { api } from "../../lib/api.ts";
import { CountryBadge } from "./CountryBadge.tsx";
import { StatusPill } from "./StatusPill.tsx";
import { useAuth } from "../../lib/auth-context.tsx";
import { useTheme } from "../../lib/theme-context.tsx";

interface Alert {
  id: string;
  title: string;
  description: string;
  severity: string;
  recommended_action?: string | null;
}

interface TopbarProps {
  projectName?: string;
  projectId?: string;
  countryCode?: keyof typeof import("../../lib/constants.ts").COUNTRY_META;
  alerts?: Alert[];
  onDismissAlert?: (alertId: string) => void;
  onProjectRenamed?: (newName: string) => void;
}

const SEVERITY_CONFIG: Record<string, { border: string; bg: string; statusPill: string }> = {
  blocking:           { border: "border-danger",     bg: "bg-danger-light",  statusPill: "blocked" },
  action_required:    { border: "border-danger/50",  bg: "bg-danger-light",  statusPill: "blocked" },
  action_recommended: { border: "border-warning/50", bg: "bg-warning-light", statusPill: "preparing" },
  informational:      { border: "border-info/50",    bg: "bg-info-light",    statusPill: "submitted" },
};

export function Topbar({ projectName, projectId, countryCode, alerts = [], onDismissAlert, onProjectRenamed }: TopbarProps) {
  const { user, logOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(projectName ?? "");
  const [saving, setSaving] = useState(false);
  const editRef = useRef<HTMLInputElement>(null);

  // Fetch streak (fields updated in last 24h) when project is loaded
  useEffect(() => {
    if (!projectId) { setStreakCount(0); return; }
    let cancelled = false;
    api.getActivityStreak(projectId)
      .then(r => { if (!cancelled) setStreakCount(r.fields_updated_today); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [projectId]);

  const initials = user
    ? user.name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
    : "??";

  const alertCount = alerts.length;

  return (
    <header className="h-12 bg-surface border-b border-border flex items-center px-2 sm:px-4 gap-2 sm:gap-3">
      <Link to="/projects" className="flex items-center gap-1 sm:gap-1.5 text-brand font-normal text-[20px] sm:text-[25px] no-underline flex-shrink-0">
        <Zap size={20} className="sm:w-[22px] sm:h-[22px]" />
        <span className="font-bold" style={{ letterSpacing: "-0.02em" }}>enereg</span>
        <span className="beta-badge hidden sm:inline">Beta</span>
      </Link>

      {projectName && (
        <>
          <span className="text-text-tertiary hidden md:inline">/</span>
          {editing ? (
            <form
              className="hidden md:flex items-center gap-1"
              onSubmit={async (e) => {
                e.preventDefault();
                const trimmed = editName.trim();
                if (!trimmed || trimmed === projectName || !projectId) {
                  setEditing(false);
                  setEditName(projectName);
                  return;
                }
                setSaving(true);
                try {
                  await api.renameProject(projectId, trimmed);
                  onProjectRenamed?.(trimmed);
                } catch (err) {
                  console.error("Rename failed:", err);
                  setEditName(projectName);
                } finally {
                  setSaving(false);
                  setEditing(false);
                }
              }}
            >
              <input
                ref={editRef}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={() => { if (!saving) { setEditing(false); setEditName(projectName); } }}
                onKeyDown={(e) => { if (e.key === "Escape") { setEditing(false); setEditName(projectName); } }}
                className="text-[16px] font-medium bg-surface-secondary border border-border rounded-lg px-2 py-0.5 outline-none focus:ring-1 focus:ring-brand/30 w-[200px] lg:w-[300px]"
                autoFocus
                disabled={saving}
              />
              <button type="submit" disabled={saving} className="p-1 text-brand hover:bg-brand/10 rounded-lg">
                <Check size={14} />
              </button>
            </form>
          ) : (
            <button
              onClick={() => { setEditName(projectName); setEditing(true); }}
              className="hidden md:flex items-center gap-1.5 text-[16px] text-text-primary font-medium truncate max-w-[200px] lg:max-w-none hover:text-brand transition-colors group"
              title="Click to rename"
            >
              {projectName}
              <Pencil size={12} className="text-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
            </button>
          )}
          {countryCode && <span className="hidden md:inline"><CountryBadge code={countryCode} /></span>}
        </>
      )}

      <div className="flex-1" />

      {/* Streak chip — shows field activity in last 24h */}
      {streakCount > 0 && (
        <Tooltip text="Fields your team updated in the last 24 hours" position="bottom">
          <div className="hidden sm:flex items-center gap-1 px-2 py-1 bg-brand-light text-brand rounded-full text-[12px] font-medium">
            <Flame size={12} />
            <span>{streakCount} today</span>
          </div>
        </Tooltip>
      )}

      {/* Alert bell — opens slide-over */}
      {alertCount > 0 && (
        <Tooltip text={`${alertCount} regulatory alert${alertCount !== 1 ? "s" : ""} needing attention`} position="bottom">
          <button
            onClick={() => setShowAlerts(!showAlerts)}
            className="flex items-center gap-1 px-2 py-1 bg-danger-light text-danger rounded-full text-[13px] font-medium"
          >
            <Bell size={12} />
            <span>{alertCount}</span>
          </button>
        </Tooltip>
      )}

      {/* User menu */}
      <div className="relative">
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="w-7 h-7 rounded-full bg-brand text-white text-[13px] font-medium flex items-center justify-center hover:bg-brand/90 transition-colors"
        >
          {initials}
        </button>

        {showMenu && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
            <div className="absolute right-0 top-9 z-50 w-52 bg-surface border border-border rounded-xl shadow-lg py-1">
              {user && (
                <div className="px-3 py-2 border-b border-border">
                  <div className="text-[14px] font-medium truncate">{user.name}</div>
                  <div className="text-[12px] text-text-tertiary truncate">{user.email}</div>
                  <div className="text-[12px] text-text-tertiary">{user.org_name} · {user.role}</div>
                </div>
              )}
              <div className="flex items-center gap-1 px-3 py-2 border-b border-border">
                <span className="text-[12px] text-text-tertiary mr-auto">Theme</span>
                {([
                  { value: "light" as const, icon: Sun, label: "Light" },
                  { value: "system" as const, icon: Monitor, label: "System" },
                  { value: "dark" as const, icon: Moon, label: "Dark" },
                ]).map(({ value, icon: Icon, label }) => (
                  <button
                    key={value}
                    title={label}
                    onClick={() => setTheme(value)}
                    className={`p-1.5 rounded-lg transition-colors ${theme === value ? "bg-brand/15 text-brand" : "text-text-tertiary hover:text-text-secondary hover:bg-surface-tertiary"}`}
                  >
                    <Icon size={14} />
                  </button>
                ))}
              </div>
              {projectId && (
                <button
                  onClick={() => { setShowMenu(false); setShowDeleteConfirm(true); }}
                  className="w-full text-left px-3 py-2 text-[14px] text-danger hover:bg-danger-light flex items-center gap-2"
                >
                  <Trash2 size={12} /> Delete project
                </button>
              )}
              <button
                onClick={async () => { await logOut(); setShowMenu(false); }}
                className="w-full text-left px-3 py-2 text-[14px] text-text-secondary hover:bg-surface-secondary flex items-center gap-2"
              >
                <LogOut size={12} /> Log out
              </button>
            </div>
          </>
        )}
      </div>

      {/* Alert slide-over panel */}
      {showAlerts && (
        <>
          <div className="fixed inset-0 z-40 bg-black/10" onClick={() => setShowAlerts(false)} />
          <div className="fixed right-0 top-0 z-50 h-full w-full max-w-sm bg-surface border-l border-border shadow-lg overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <h2 className="text-[17px] font-medium">Regulatory Alerts</h2>
              <button onClick={() => setShowAlerts(false)} className="p-1 hover:bg-surface-secondary rounded-lg">
                <X size={16} className="text-text-tertiary" />
              </button>
            </div>

            <div className="px-4 py-3 space-y-3">
              {/* Regulatory currency indicator */}
              <div className="flex flex-col gap-0.5 px-3 py-2 bg-surface-secondary rounded-xl text-[13px] text-text-tertiary">
                <span className="flex items-center gap-2"><span className="text-brand">●</span>Data current as of March 2026</span>
                <span>Last gazette check: 2026-03-20</span>
              </div>

              {alerts.length === 0 ? (
                <div className="bg-brand-light rounded-xl px-4 py-3 text-[15px] text-brand">
                  All clear — no regulatory alerts.
                </div>
              ) : (
                alerts.map(alert => {
                  const config = SEVERITY_CONFIG[alert.severity] ?? SEVERITY_CONFIG.informational!;
                  return (
                    <div key={alert.id} className={`border ${config.border} ${config.bg} rounded-2xl px-3.5 py-3`}>
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <StatusPill status={config.statusPill as any} />
                            <span className="text-[15px] font-medium truncate">{alert.title}</span>
                          </div>
                          <p className="text-[13px] text-text-secondary leading-relaxed">{alert.description}</p>
                          {alert.recommended_action && (
                            <div className="mt-2 pt-2 border-t border-black/5">
                              <span className="text-[11px] font-medium text-text-tertiary uppercase tracking-wider">Recommended</span>
                              <p className="text-[13px] text-text-primary mt-0.5 font-medium">{alert.recommended_action}</p>
                            </div>
                          )}
                        </div>
                        {onDismissAlert && (
                          <button
                            onClick={() => onDismissAlert(alert.id)}
                            className="text-[12px] text-text-tertiary hover:text-text-secondary flex-shrink-0 mt-0.5"
                          >
                            Dismiss
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
      {/* Delete confirmation dialog */}
      {showDeleteConfirm && projectId && (
        <>
          <div className="fixed inset-0 z-50 bg-black/30" onClick={() => !deleting && setShowDeleteConfirm(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-surface rounded-2xl shadow-xl border border-border max-w-sm w-full p-5">
              <h3 className="text-[17px] font-medium mb-2">Delete project?</h3>
              <p className="text-[14px] text-text-secondary mb-4">
                This will permanently delete <strong>{projectName}</strong> and all its licenses, fields, documents, and compliance records. This cannot be undone.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-3 py-1.5 text-[14px] text-text-secondary hover:bg-surface-secondary rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await api.deleteProject(projectId);
                      navigate("/projects");
                    } catch (e) {
                      console.error("Delete failed:", e);
                      setDeleting(false);
                    }
                  }}
                  disabled={deleting}
                  className="px-3 py-1.5 text-[14px] font-medium text-white bg-danger hover:bg-danger/90 rounded-lg disabled:opacity-50"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
}
